/**
 * Where a request actually goes.
 *
 * Normally: the network. In the offline demo (`?demo`) the game host runs in a
 * worker in this same tab, and every REST call and every socket has to reach it
 * instead — without a second REST client, a second socket hook, or a `if (demo)`
 * at forty call sites.
 *
 * So there are exactly two seams, and this is both of them. `api/games.ts` and
 * `api/health.ts` fetch through `httpFetch`; `ws/useSeatSocket.ts` and
 * `ws/useGameSocket.ts` open through `openSocket`. Nothing else changes, and
 * with no demo installed both are the platform's own `fetch` and `WebSocket`.
 *
 * The routing key is the URL scheme, not a flag: the demo's base URL is
 * `demo://badger-sentinel` (`demo/config.ts`), which no network stack resolves,
 * so a demo request that somehow escaped this module would fail loudly rather
 * than silently reach a real server.
 */

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

/**
 * The demo socket implements the members `useSeatSocket` actually touches —
 * `readyState`, `send`, `close`, and the three `on*` handlers — and is cast to
 * `WebSocket` once, at the seam, rather than widening the hook's own types.
 */
export type SocketFactory = (url: string) => WebSocket;

let httpOverride: FetchLike | null = null;
let socketOverride: SocketFactory | null = null;

/** Installed by the demo bridge once its worker is ready. Null uninstalls it. */
export function setHttpTransport(fetchLike: FetchLike | null): void {
  httpOverride = fetchLike;
}

export function setSocketTransport(factory: SocketFactory | null): void {
  socketOverride = factory;
}

function isLocalUrl(url: string): boolean {
  return url.startsWith('demo://');
}

export function httpFetch(url: string, init: RequestInit = {}): Promise<Response> {
  if (httpOverride && isLocalUrl(url)) return httpOverride(url, init);
  return fetch(url, init);
}

export function openSocket(url: string): WebSocket {
  if (socketOverride && isLocalUrl(url)) return socketOverride(url);
  return new WebSocket(url);
}
